import React from 'react';

import { noop } from '@schibsted-nmp/advertising-shared';

export namespace Performance {
  export function useImpression({ elementRef }: useImpression.Options) {
    const [hasTriggered, setHasTriggered] = React.useState(false);

    React.useEffect(() => {
      if (hasTriggered) return;
      if (!elementRef.current) return;

      setHasTriggered(true);
    }, [elementRef, hasTriggered]);

    return hasTriggered;
  }

  export namespace useImpression {
    export type Options = {
      elementRef: React.RefObject<HTMLElement>;
    };
  }

  export function useViewableImpression({
    elementRef,
    threshold = 0.5,
    minimumDuration = 1000
  }: useViewableImpression.Options) {
    const [hasTriggered, setHasTriggered] = React.useState(false);

    React.useEffect(() => {
      if (!('IntersectionObserver' in window)) return noop;
      if (hasTriggered) return noop;

      const currentElement = elementRef.current;
      if (!currentElement) return noop;

      let timer: number | undefined;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;

          if (entry.isIntersecting) {
            timer = window.setTimeout(() => {
              setHasTriggered(true);
              observer.unobserve(currentElement);
            }, minimumDuration);
          } else {
            window.clearTimeout(timer);
            timer = undefined;
          }
        },
        { threshold }
      );

      observer.observe(currentElement);

      return () => {
        window.clearTimeout(timer);
        observer.unobserve(currentElement);
      };
    }, [elementRef, threshold, minimumDuration, hasTriggered]);

    return hasTriggered;
  }

  export namespace useViewableImpression {
    export type Options = {
      elementRef: React.RefObject<HTMLElement>;
      threshold?: number;
      minimumDuration?: number;
    };
  }
}
